import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause } from 'lucide-react';
import { Slider } from '@/components/ui/slider';

const SOUNDS = [
  { id: 'rain', name: 'Rain', emoji: '🌧️', color: '#87CEEB' },
  { id: 'ocean', name: 'Ocean Waves', emoji: '🌊', color: '#4682B4' },
  { id: 'wind', name: 'Gentle Wind', emoji: '💨', color: '#B0C4DE' },
  { id: 'water', name: 'Fast Flowing Water', emoji: '💧', color: '#40E0D0' },
  { id: 'birds', name: 'Forest Birds', emoji: '🐦', color: '#228B22' }
];

const AudioRainmaker = () => {
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const [volumes, setVolumes] = useState(Object.fromEntries(SOUNDS.map(s => [s.id, 0])));
  const audioContextRef = useRef(null);
  const soundEnginesRef = useRef({});
  const intervalsRef = useRef({});

  useEffect(() => {
    return () => {
      stopAllSounds();
    };
  }, []);

  const stopAllSounds = () => {
    // Clear all intervals
    Object.values(intervalsRef.current).forEach(interval => clearInterval(interval));
    intervalsRef.current = {};
    
    // Stop all sound engines
    Object.values(soundEnginesRef.current).forEach(engine => {
      if (engine.stop) engine.stop();
    });
    soundEnginesRef.current = {};
    
    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  };

  const createRain = (ctx, masterGain) => {
    // Rain: Multiple layers of filtered noise + individual drop sounds
    const rainGain = ctx.createGain();
    rainGain.gain.value = 0;
    rainGain.connect(masterGain);

    // Base rain texture - filtered noise
    const bufferSize = 2 * ctx.sampleRate;
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    noise.loop = true;

    // Bandpass filter to shape rain sound
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 3000;
    filter.Q.value = 0.5;

    // High shelf to add brightness
    const highShelf = ctx.createBiquadFilter();
    highShelf.type = 'highshelf';
    highShelf.frequency.value = 4000;
    highShelf.gain.value = 3;

    noise.connect(filter);
    filter.connect(highShelf);
    highShelf.connect(rainGain);
    noise.start();

    // Individual rain drops
    const dropInterval = setInterval(() => {
      if (rainGain.gain.value > 0.05) {
        const drop = ctx.createOscillator();
        const dropGain = ctx.createGain();
        drop.type = 'sine';
        drop.frequency.value = 2000 + Math.random() * 3000;
        dropGain.gain.setValueAtTime(0.02 * rainGain.gain.value, ctx.currentTime);
        dropGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.03);
        drop.connect(dropGain);
        dropGain.connect(masterGain);
        drop.start();
        drop.stop(ctx.currentTime + 0.03);
      }
    }, 30);

    intervalsRef.current.rain = dropInterval;

    return {
      gainNode: rainGain,
      stop: () => {
        clearInterval(dropInterval);
        noise.stop();
      }
    };
  };

  const createOcean = (ctx, masterGain) => {
    // Ocean: Rhythmic waves with whooshing sounds
    const oceanGain = ctx.createGain();
    oceanGain.gain.value = 0;
    oceanGain.connect(masterGain);

    // Base ocean texture
    const bufferSize = 2 * ctx.sampleRate;
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    noise.loop = true;

    // Low pass for deep ocean sound
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 500;
    filter.Q.value = 1;

    // Modulate the volume for wave effect
    const waveGain = ctx.createGain();
    
    // LFO for wave rhythm
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.1; // Slow waves every ~10 seconds
    
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.4;
    
    lfo.connect(lfoGain);
    lfoGain.connect(waveGain.gain);
    
    noise.connect(filter);
    filter.connect(waveGain);
    waveGain.connect(oceanGain);
    
    noise.start();
    lfo.start();

    // Wave crashes
    const waveCrash = () => {
      if (oceanGain.gain.value < 0.1) return;
      
      const crashNoise = ctx.createBufferSource();
      crashNoise.buffer = noiseBuffer;
      
      const crashFilter = ctx.createBiquadFilter();
      crashFilter.type = 'highpass';
      crashFilter.frequency.value = 800;
      
      const crashGain = ctx.createGain();
      crashGain.gain.setValueAtTime(0, ctx.currentTime);
      crashGain.gain.linearRampToValueAtTime(oceanGain.gain.value * 0.4, ctx.currentTime + 0.5);
      crashGain.gain.linearRampToValueAtTime(0, ctx.currentTime + 3);
      
      crashNoise.connect(crashFilter);
      crashFilter.connect(crashGain);
      crashGain.connect(masterGain);
      crashNoise.start();
      crashNoise.stop(ctx.currentTime + 3);
    };

    const waveInterval = setInterval(waveCrash, 8000);
    intervalsRef.current.ocean = waveInterval;

    return {
      gainNode: oceanGain,
      stop: () => {
        clearInterval(waveInterval);
        noise.stop();
        lfo.stop();
      }
    };
  };

  const createWind = (ctx, masterGain) => {
    // Wind: Slow filtered noise with modulation
    const windGain = ctx.createGain();
    windGain.gain.value = 0;
    windGain.connect(masterGain);

    const bufferSize = 2 * ctx.sampleRate;
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    noise.loop = true;

    // Bandpass for wind character
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 400;
    filter.Q.value = 2;

    // Modulate filter frequency for "gusting" effect
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.2;
    
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 200;
    
    lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);

    // Volume modulation
    const volLfo = ctx.createOscillator();
    volLfo.type = 'sine';
    volLfo.frequency.value = 0.15;
    
    const volLfoGain = ctx.createGain();
    volLfoGain.gain.value = 0.3;
    
    const modGain = ctx.createGain();
    modGain.gain.value = 0.5;
    
    volLfo.connect(volLfoGain);
    volLfoGain.connect(modGain.gain);

    noise.connect(filter);
    filter.connect(modGain);
    modGain.connect(windGain);
    
    noise.start();
    lfo.start();
    volLfo.start();

    return {
      gainNode: windGain,
      stop: () => {
        noise.stop();
        lfo.stop();
        volLfo.stop();
      }
    };
  };

  const createWater = (ctx, masterGain) => {
    // Fast flowing water: Bubbling, splashing sounds
    const waterGain = ctx.createGain();
    waterGain.gain.value = 0;
    waterGain.connect(masterGain);

    // Base rushing water
    const bufferSize = 2 * ctx.sampleRate;
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    noise.loop = true;

    // High pass for bright water sound
    const highpass = ctx.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = 1000;

    // Bandpass for main character
    const bandpass = ctx.createBiquadFilter();
    bandpass.type = 'bandpass';
    bandpass.frequency.value = 2500;
    bandpass.Q.value = 0.5;

    noise.connect(highpass);
    highpass.connect(bandpass);
    bandpass.connect(waterGain);
    noise.start();

    // Bubbles
    const createBubble = () => {
      if (waterGain.gain.value < 0.05) return;
      
      const bubble = ctx.createOscillator();
      bubble.type = 'sine';
      bubble.frequency.setValueAtTime(400 + Math.random() * 400, ctx.currentTime);
      bubble.frequency.exponentialRampToValueAtTime(800 + Math.random() * 600, ctx.currentTime + 0.05);
      
      const bubbleGain = ctx.createGain();
      bubbleGain.gain.setValueAtTime(waterGain.gain.value * 0.15, ctx.currentTime);
      bubbleGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
      
      bubble.connect(bubbleGain);
      bubbleGain.connect(masterGain);
      bubble.start();
      bubble.stop(ctx.currentTime + 0.08);
    };

    const bubbleInterval = setInterval(createBubble, 80);
    intervalsRef.current.water = bubbleInterval;

    return {
      gainNode: waterGain,
      stop: () => {
        clearInterval(bubbleInterval);
        noise.stop();
      }
    };
  };

  const createBirds = (ctx, masterGain) => {
    // Birds: Actual chirping patterns with varied frequencies
    const birdsGain = ctx.createGain();
    birdsGain.gain.value = 0;
    birdsGain.connect(masterGain);

    const chirp = (baseFreq, duration, pattern) => {
      if (ctx.state === 'closed') return;
      if (birdsGain.gain.value < 0.05) return;

      const osc = ctx.createOscillator();
      osc.type = 'sine';
      
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, ctx.currentTime);
      
      // Different chirp patterns
      const now = ctx.currentTime;
      if (pattern === 'tweet') {
        // Simple tweet: up then down
        osc.frequency.setValueAtTime(baseFreq, now);
        osc.frequency.linearRampToValueAtTime(baseFreq * 1.5, now + duration * 0.3);
        osc.frequency.linearRampToValueAtTime(baseFreq * 0.9, now + duration);
        gain.gain.linearRampToValueAtTime(birdsGain.gain.value * 0.2, now + 0.01);
        gain.gain.linearRampToValueAtTime(birdsGain.gain.value * 0.15, now + duration * 0.5);
        gain.gain.linearRampToValueAtTime(0, now + duration);
      } else if (pattern === 'warble') {
        // Warbling: rapid frequency changes
        for (let i = 0; i < 6; i++) {
          const t = now + (i * duration / 6);
          osc.frequency.setValueAtTime(baseFreq * (1 + (i % 2) * 0.3), t);
        }
        gain.gain.linearRampToValueAtTime(birdsGain.gain.value * 0.15, now + 0.01);
        gain.gain.setValueAtTime(birdsGain.gain.value * 0.15, now + duration * 0.8);
        gain.gain.linearRampToValueAtTime(0, now + duration);
      } else if (pattern === 'trill') {
        // Trill: very fast alternation
        const trillSpeed = 0.03;
        for (let t = 0; t < duration; t += trillSpeed) {
          osc.frequency.setValueAtTime(baseFreq * (1 + 0.1 * (Math.floor(t / trillSpeed) % 2)), now + t);
        }
        gain.gain.linearRampToValueAtTime(birdsGain.gain.value * 0.12, now + 0.01);
        gain.gain.linearRampToValueAtTime(0, now + duration);
      }
      
      osc.connect(gain);
      gain.connect(masterGain);
      osc.start(now);
      osc.stop(now + duration + 0.1);
    };

    // Track follow-up chirp timeouts so they can be cancelled on stop()
    const chirpTimeouts = [];

    // Multiple bird calls
    const birdCall = () => {
      const patterns = ['tweet', 'warble', 'trill'];
      const pattern = patterns[Math.floor(Math.random() * patterns.length)];
      const baseFreq = 1800 + Math.random() * 2000; // Varied pitch for different "birds"
      const duration = 0.1 + Math.random() * 0.3;

      chirp(baseFreq, duration, pattern);

      // Sometimes do a sequence
      if (Math.random() < 0.4) {
        const t1 = setTimeout(() => chirp(baseFreq * (0.9 + Math.random() * 0.2), duration, pattern), duration * 1000 + 50);
        chirpTimeouts.push(t1);
      }
      if (Math.random() < 0.2) {
        const t2 = setTimeout(() => chirp(baseFreq * (0.9 + Math.random() * 0.2), duration, pattern), duration * 2000 + 100);
        chirpTimeouts.push(t2);
      }
    };

    const birdInterval = setInterval(() => {
      if (Math.random() < 0.3) { // 30% chance every 500ms
        birdCall();
      }
    }, 500);

    intervalsRef.current.birds = birdInterval;

    return {
      gainNode: birdsGain,
      stop: () => {
        clearInterval(birdInterval);
        chirpTimeouts.forEach(t => clearTimeout(t));
        chirpTimeouts.length = 0;
      }
    };
  };

  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioContextRef.current;
      
      // Master gain
      const masterGain = ctx.createGain();
      masterGain.gain.value = 0.7;
      masterGain.connect(ctx.destination);

      // Create all sound engines
      soundEnginesRef.current = {
        rain: createRain(ctx, masterGain),
        ocean: createOcean(ctx, masterGain),
        wind: createWind(ctx, masterGain),
        water: createWater(ctx, masterGain),
        birds: createBirds(ctx, masterGain)
      };
    }
  }, []);

  const togglePlay = () => {
    if (!isPlaying) {
      initAudio();
      setIsPlaying(true);
    } else {
      stopAllSounds();
      setIsPlaying(false);
      setVolumes(Object.fromEntries(SOUNDS.map(s => [s.id, 0])));
    }
  };

  const handleVolumeChange = (soundId, value) => {
    const vol = value[0] / 100;
    setVolumes(prev => ({ ...prev, [soundId]: value[0] }));
    
    if (soundEnginesRef.current[soundId]) {
      soundEnginesRef.current[soundId].gainNode.gain.setTargetAtTime(
        vol * 0.8, 
        audioContextRef.current.currentTime, 
        0.1
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E0F7FA] to-[#B2EBF2] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-400 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-8">
        <header className="flex justify-between items-center mb-12">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-4xl font-bold text-[#2D3748]">Audio Rainmaker</h1>
          
          <button
            data-testid="toggle-play"
            onClick={togglePlay}
            className={`px-8 py-3 rounded-full font-bold transition-all duration-300 shadow-lg flex items-center gap-2 ${
              isPlaying ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-[#81B29A] hover:bg-[#6A9B84] text-white'
            }`}
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            {isPlaying ? 'Stop All' : 'Start'}
          </button>
        </header>

        <p className="text-center text-[#4A5568] mb-8">
          {isPlaying 
            ? 'Adjust the sliders to mix your perfect nature soundscape' 
            : 'Press Start, then adjust the sliders to mix different nature sounds'}
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {SOUNDS.map(sound => (
            <div
              key={sound.id}
              className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100"
              style={{ borderLeftWidth: '8px', borderLeftColor: sound.color }}
            >
              <div className="flex items-center gap-4 mb-4">
                <span className="text-4xl">{sound.emoji}</span>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#2D3748]">{sound.name}</h3>
                  <p className="text-2xl font-bold" style={{ color: sound.color }}>{volumes[sound.id]}%</p>
                </div>
                {volumes[sound.id] > 0 && isPlaying && (
                  <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                )}
              </div>
              
              <Slider
                data-testid={`${sound.id}-slider`}
                value={[volumes[sound.id]]}
                onValueChange={(value) => handleVolumeChange(sound.id, value)}
                max={100}
                step={1}
                disabled={!isPlaying}
                className={!isPlaying ? 'opacity-50' : ''}
              />
            </div>
          ))}
        </div>

        {!isPlaying && (
          <p className="text-center text-[#4A5568] mt-8 text-sm">
            💡 Press Start to enable the sound sliders
          </p>
        )}
      </div>
    </div>
  );
};

export default AudioRainmaker;
